// Phase 11c — operator-only admin surface for caregiver_guarantee_policy.
//
// Pure structural mirror of cancellation-policy-admin (10b), differing
// only in (a) field name `payPct` instead of `chargePct` (10b-1 / 11-4
// semantic split — what we PAY caregiver vs what we CHARGE buyer). Like
// 10b it is keyed on (buyerType, hoursBeforeThreshold) — the buyerType axis
// was added 2026-10-28 so the homecare guarantee can move independently.
//
// Lead-time enforcement reuses the shared `LEAD_TIME_DAYS = 14`
// constant from `apps/backend/src/shared/lead-time/lead-time.ts`
// per 11c-6 + 10b-6.
import { z } from 'zod';
import { BuyerTypeSchema } from './enums.schema.js';
// ===== shared pieces (same shapes as 10b for cross-mutation consistency) =====
const IsoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const PayPctSchema = z
    .string()
    .regex(/^\d{1,3}(\.\d{1,2})?$/, 'payPct must be a decimal 0..100 with up to 2 dp')
    .refine((s) => {
    const n = Number.parseFloat(s);
    return n >= 0 && n <= 100;
}, 'payPct must be between 0 and 100');
const HoursThresholdSchema = z.number().int().positive().max(168); // ≤ 1 week
const ReasonSchema = z
    .string()
    .min(10, 'Reason must be at least 10 characters — document why')
    .max(500);
const LeadTimeOverrideSchema = z
    .object({
    overrideLeadTime: z.boolean().optional(),
    overrideReason: z.string().min(10).max(500).optional(),
})
    .refine((v) => !v.overrideLeadTime || (v.overrideReason !== undefined && v.overrideReason.length >= 10), { message: 'overrideReason is required (>=10 chars) when overrideLeadTime is true' });
// ===== caregiver-guarantee-policy admin =====
export const AdminCaregiverGuaranteePolicyCreateRequestSchema = z
    .object({
    buyerType: BuyerTypeSchema,
    hoursBeforeThreshold: HoursThresholdSchema,
    payPct: PayPctSchema,
    effectiveFrom: IsoDateSchema,
    reason: ReasonSchema,
})
    .and(LeadTimeOverrideSchema);
export const AdminCaregiverGuaranteePolicySupersedeRequestSchema = z
    .object({
    newPayPct: PayPctSchema,
    effectiveFrom: IsoDateSchema,
    reason: ReasonSchema,
})
    .and(LeadTimeOverrideSchema);
export const AdminCaregiverGuaranteePolicyDeprecateRequestSchema = z
    .object({
    effectiveTo: IsoDateSchema,
    reason: ReasonSchema,
})
    .and(LeadTimeOverrideSchema);
// v0/rate-provenance Phase 4 — in-place edit of an UNUSED guarantee policy.
// VALUE field only (payPct — matches what supersede changes); identity
// (buyerType, hoursBeforeThreshold) + effective dates stay frozen. No reason / lead-time /
// audit. Server-gated to inUse=false.
export const AdminCaregiverGuaranteePolicyEditRequestSchema = z.object({
    payPct: PayPctSchema,
});
export const AdminCaregiverGuaranteePolicyResponseSchema = z.object({
    id: z.string().uuid(),
    buyerType: BuyerTypeSchema,
    hoursBeforeThreshold: z.number().int().positive(),
    payPct: z.string(),
    effectiveFrom: IsoDateSchema,
    effectiveTo: IsoDateSchema.nullable(),
    isCurrent: z.boolean(),
    // v0/rate-provenance — has this policy priced a shift (via a PayoutLine)?
    inUse: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminCaregiverGuaranteePolicyListQuerySchema = z.object({
    buyerType: BuyerTypeSchema.optional(),
    includeHistorical: z.coerce.boolean().default(false),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const AdminCaregiverGuaranteePolicyListResponseSchema = z.object({
    items: z.array(AdminCaregiverGuaranteePolicyResponseSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
// ===== change log (read-only) =====
export const AdminCaregiverGuaranteePolicyChangeActionSchema = z.enum([
    'CREATED',
    'SUPERSEDED',
    'DEPRECATED',
]);
export const AdminCaregiverGuaranteePolicyChangeLogEntrySchema = z.object({
    id: z.string().uuid(),
    actorUserId: z.string().uuid(),
    actorEmail: z.string(),
    ts: z.string(),
    policyId: z.string().uuid(),
    action: AdminCaregiverGuaranteePolicyChangeActionSchema,
    oldValue: z.unknown().nullable(),
    newValue: z.unknown().nullable(),
    reason: z.string(),
    leadTimeOverrideReason: z.string().nullable(),
    appliedEffectiveFrom: IsoDateSchema,
});
export const AdminCaregiverGuaranteePolicyChangeLogListQuerySchema = z.object({
    policyId: z.string().uuid().optional(),
    action: AdminCaregiverGuaranteePolicyChangeActionSchema.optional(),
    actorUserId: z.string().uuid().optional(),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(50),
});
export const AdminCaregiverGuaranteePolicyChangeLogListResponseSchema = z.object({
    items: z.array(AdminCaregiverGuaranteePolicyChangeLogEntrySchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
//# sourceMappingURL=caregiver-guarantee-policy-admin.schema.js.map